if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Initialize auto-scroll
        this.initAutoScroll();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, isAutoScroll = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (isAutoScroll) return; // Fix: Prevent window from scrolling to top during autoplay
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initAutoScroll() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const slider = viewer.slider;
        if (!slider) return;

        const slides = Array.from(viewer.querySelectorAll('[id^="Slide-"]'));
        if (slides.length < 2) return;

        this.autoScrollDelay = 3500; // 3.5 seconds
        this.autoScrollInterval = null;

        const getNextIndex = () => {
          const currentPage = viewer.currentPage;
          if (currentPage !== undefined && currentPage !== null) {
            return currentPage % slides.length;
          }
          let activeIndex = 0;
          slides.forEach((slide, index) => {
            if (slide.classList.contains('is-active')) {
              activeIndex = index;
            }
          });
          return (activeIndex + 1) % slides.length;
        };

        const scrollToNextSlide = () => {
          // Verify slider is still active and scrollable
          if (slider.clientWidth >= slider.scrollWidth) return;

          const nextIndex = getNextIndex();
          const nextSlide = slides[nextIndex];
          if (!nextSlide) return;

          const mediaId = nextSlide.dataset.mediaId;
          this.setActiveMedia(mediaId, false, true);
        };

        const start = () => {
          // Only auto-scroll if slider is actually scrollable (e.g. mobile view or desktop carousel view)
          if (slider.clientWidth >= slider.scrollWidth) return;
          if (this.autoScrollInterval) return;
          this.autoScrollInterval = setInterval(scrollToNextSlide, this.autoScrollDelay);
        };

        const stop = () => {
          if (this.autoScrollInterval) {
            clearInterval(this.autoScrollInterval);
            this.autoScrollInterval = null;
          }
        };

        // Initialize auto-scroll
        start();

        // Pause auto-scroll on manual interaction/hover
        this.addEventListener('mouseenter', stop);
        this.addEventListener('mouseleave', start);
        this.addEventListener('touchstart', stop, { passive: true });
        this.addEventListener('touchend', start, { passive: true });
        this.addEventListener('focusin', stop);
        this.addEventListener('focusout', start);

        // Pause when tab is inactive to save browser cycles
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            stop();
          } else {
            start();
          }
        });
      }
    }
  );
}
